import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from '@angular/common/http';
import { HelpersService } from "./helpers.service";
import { catchError, concatMap, delay, map, mergeMap, Observable, of, retryWhen, throwError } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { PaymentIntent } from "@stripe/stripe-js";
import { polandStateCodes } from "../models/user";
import { MatDialog } from "@angular/material/dialog";
@Injectable()

export class OrdersService extends HelpersService {
    // public wizardTitle: any;
    public mapAddress: {[index: string]: any} = {
        getProvinceLevelOne: function(self: any, place: any) {
            return self.getFieldInfo(place, ['administrative_area_level_1'], 'short_name');
        },
        getProvinceLevelTwo: function(self: any, place: any) {
            return self.getFieldInfo(place, ['administrative_area_level_2'], 'short_name');
        },
        getProvincePoland: function(self: any, place: any) {
            const googleProvince = self.getFieldInfo(place, ['administrative_area_level_1'], 'short_name').toLowerCase();
            let stateCode = null;
            if(Object.keys(self.polishProvinces).find(key => key.toLowerCase() === googleProvince)) {
                stateCode = self.polishProvinces[googleProvince];
            }
            if (!stateCode) {
                if(Object.keys(polandStateCodes).find(key => key.toLowerCase() === googleProvince)) {
                    stateCode = polandStateCodes[googleProvince];
                }
            }
            return stateCode;
        },
        getCitySM: function(self: any, place: any) {
            return self.getFieldInfo(place, ['country'], 'long_name');
        }
    }

    constructor(router: Router, http: HttpClient, translate: TranslateService, dialog: MatDialog) {
        super(router, http, translate, dialog)
    }

    public checkOrderStatus(orderId: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/status`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getSummary(orderId: string) {
        return this.http.get(`${this.config.BASEURL_ACCOUNT_API}/v2/secured/orders/${orderId}/summary`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getTranslationList(string: string) {
        const stringToArray = string.split(",");
        return stringToArray;
    }

    public sendVehicleInformation(orderId: string, req: any) {
        return this.http.patch(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/vehicle`, req, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getContractDurations(id: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${id}/summary`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public sendContractDurations(id: string, req: any) {
        return this.http.patch(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${id}/duration`, req, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }), catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getDocuments(orderId: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/agreements`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public downloadDocuments(orderId: String, type: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/download-agreement?documentType=${type}`, this.getHTTPHeaderPDF(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getDocumentsModal(orderId: String, type: string) {
        return `${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/download-agreement?documentType=${type}`;
    }

    public sendConsents(orderId: string, req: any) {
        return this.http.patch(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/consents`, req, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getPaymentMethods(orderId: any): Observable<PaymentIntent> {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/payments/payment-methods`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            }),
            retryWhen((tries) =>
                tries.pipe(
                    mergeMap((e, i) => {
                        if (i < this.config.PAYMENT_MAX_RETRIES && e.status === 409) {
                            delay(this.config.PAYMENT_RETRY_DELAY_MS)
                        }
                        return throwError(e);
                    })
                )
            )
        )
    }

    public createPaymentIntent(orderId: any, req: any): Observable<PaymentIntent> {
        return this.http.post(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/payments`, req, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            }),
            retryWhen((tries) =>
                tries.pipe(
                    mergeMap((e, i) => {
                        if (i < this.config.PAYMENT_MAX_RETRIES && e.status === 409) {
                            delay(this.config.PAYMENT_RETRY_DELAY_MS)
                        }
                        return throwError(e);
                    })
                )
            )
        )
    }

    public confirmPayment(orderId: any, intent: any, req?: any): Observable<any> {
        return this.http.post(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/payments/confirm?intent_id=${intent}`, req ? req : {automatic_renewal: false}, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            }),
            retryWhen((tries) =>
                tries.pipe(
                    mergeMap((e, i) => {
                        if (i < this.config.PAYMENT_MAX_RETRIES && e.status === 409) {
                            return of(null);
                        }
                        return throwError(e);
                    })
                )
            )
        )
    }

    public createSubscriptionIntent(orderId: any, paymentMethodId: string): Observable<PaymentIntent> {
      return this.http.post(`${this.config.BASEURL_ORDER_API}/v1/secured/assets/${orderId}/automatic-renewal`, { paymentMethodId: paymentMethodId }, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public confirmSubscriptionIntent(assetId: any, intent: any): Observable<PaymentIntent> {
        return this.http.post(`${this.config.BASEURL_ORDER_API}/v1/secured/assets/${assetId}/automatic-renewal/confirm?intent_id=${intent}`, {}, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public deleteSubscriptionFlag(assetId: any): Observable<PaymentIntent> {
        return this.http.delete(`${this.config.BASEURL_ORDER_API}/v1/secured/assets/${assetId}/automatic-renewal`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getCustomerInfo(orderId: string, type: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/payments/billing-details?type=${type.toUpperCase()}`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                res = { "billingDetails":  res };
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public chooseOrderId(orderObj: any, isOrder: boolean = true) {
        let id = "";
        if(isOrder) {
            switch(orderObj.NE__OrderId__r.Technical_Channel__c){
                case ("SDP-ORDERS"):
                    id = orderObj.NE__CatalogItem__r.NE__Id__c ? orderObj.NE__CatalogItem__r.NE__Id__c : orderObj.NE__CatalogItem__r.NE__ProductId__r.b2w__External_Id__c;
                    break;
                case ("B2W-MC"):
                    id = orderObj.NE__CatalogItem__r.NE__ProductId__r.b2w__External_Id__c ? orderObj.NE__CatalogItem__r.NE__ProductId__r.b2w__External_Id__c : orderObj.NE__CatalogItem__r.NE__Id__c;
                    break;
                default:
                    id = orderObj.NE__CatalogItem__r.NE__Id__c ? orderObj.NE__CatalogItem__r.NE__Id__c : orderObj.NE__CatalogItem__r.NE__ProductId__r.b2w__External_Id__c;
                    break;
            }
        } else {
            switch(orderObj.technical_channel){
                case ("SDP-ORDERS"):
                    id = orderObj.catalog_item.NE__Id__c ? orderObj.catalog_item.NE__Id__c : orderObj.catalog_item.NE__ProductId__r.b2w__External_Id__c;
                    break;
                case ("B2W-MC"):
                    id = orderObj.catalog_item.NE__ProductId__r.b2w__External_Id__c ? orderObj.catalog_item.NE__ProductId__r.b2w__External_Id__c : orderObj.catalog_item.NE__Id__c;
                    break;
                default:
                    id = orderObj.catalog_item.NE__Id__c ? orderObj.catalog_item.NE__Id__c : orderObj.catalog_item.NE__ProductId__r.b2w__External_Id__c;
                    break;
            }
        }
        return id;
    }

    public sendGIProposal(orderId: any, req: any): Observable<PaymentIntent> {
        return this.http.post(`${this.config.BASEURL_ORDER_API}/v1/secured/orders/${orderId}/insurance/proposal`, req, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }
}
